// src/context/AppContext.js
import React,{
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from '../lib/toast';
import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from './AuthContext';

// API Configuration - Call hub-backend directly (port 3001)
const API_BASE_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
const API_PATH = '/api';

// Create context
export const AppContext = createContext();

// Debounce helper
const debounce = (fn, delay = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

// Context provider component
export const AppProvider = ({ children }) => {
  const { apiRequest, isAuthenticated, isAuthLoading } = useAuth();
  const [devices, setDevices] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initializingLockMac, setInitializingLockMac] = useState(null);

  const hasFetchedOnce = useRef(false);

  // State for configuration
  const [config, setConfig] = useState({});

  // State for smart locks
  const [smartLocks, setSmartLocks] = useState([]);

  // Smart lock access log events from WebSocket: { [lockMac]: { version, newRecords } }
  const [lockAccessLogEvents, setLockAccessLogEvents] = useState({});

  // Fetch devices - using the new API format where each gang is a separate device
  const fetchDevices = useCallback(async () => {
    if (!isAuthenticated) return [];

    try {
      const { response, data } = await apiRequest(`${API_BASE_URL}${API_PATH}/devices`);

      if (!response.ok) {
        throw new Error('Failed to fetch devices');
      }

      // Store devices as an array (new format)
      setDevices(data);

      return data;
    } catch (error) {
      console.error('Error fetching devices:', error);
      if (isAuthenticated) {
        toast.error('Failed to load devices');
      }
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Fetch scenes
  const fetchScenes = useCallback(async () => {
    if (!isAuthenticated) return [];

    try {
      const { response, data } = await apiRequest(`${API_BASE_URL}${API_PATH}/scenes`);

      if (!response.ok) {
        throw new Error('Failed to fetch scenes');
      }

      setScenes(data);
      return data;
    } catch (error) {
      console.error('Error fetching scenes:', error);
      if (isAuthenticated) {
        toast.error('Failed to load scenes');
      }
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Fetch configuration
  const fetchConfig = useCallback(async () => {
    if (!isAuthenticated) return {};

    try {
      const { response, data } = await apiRequest(`${API_BASE_URL}${API_PATH}/config`);

      if (!response.ok) {
        throw new Error('Failed to fetch configuration');
      }

      setConfig(data);
      return data;
    } catch (error) {
      console.error('Error fetching configuration:', error);
      if (isAuthenticated) {
        toast.error('Failed to load configuration');
      }
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Fetch smart locks
  const fetchSmartLocks = useCallback(async () => {
    if (!isAuthenticated) return [];

    try {
      const { response, data } = await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks`);

      if (!response.ok) {
        throw new Error('Failed to fetch smart locks');
      }

      // Handle both array and object response formats
      const locks = Array.isArray(data) ? data : (data.smart_locks || data.locks || []);
      setSmartLocks(locks);
      return locks;
    } catch (error) {
      console.error('Error fetching smart locks:', error);
      if (isAuthenticated) {
        toast.error('Failed to load smart locks');
      }
      // Set empty array on error to prevent map errors
      setSmartLocks([]);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((data) => {
    try {
      const parsedData = data;
      // console.log('📡 WebSocket message received:', parsedData);

      if (parsedData.type === 'deviceUpdate') {
        // For the new device format (array of devices with embedded status)
        setDevices(prevDevices => {
          return prevDevices.map(device => {
            if (device.id === parsedData.deviceId) {
              // Update controls with new values if they exist
              const updatedControls = device.controls ?
                device.controls.map(control => {
                  if (parsedData.status[control.property] !== undefined) {
                    return {
                      ...control,
                      value: parsedData.status[control.property]
                    };
                  }
                  return control;
                }) :
                device.controls;

              // Update sensors with new values if they exist
              const updatedSensors = device.sensors ?
                device.sensors.map(sensor => {
                  if (parsedData.status[sensor.property] !== undefined) {
                    return {
                      ...sensor,
                      value: parsedData.status[sensor.property]
                    };
                  }
                  return sensor;
                }) :
                device.sensors;

              // Return updated device
              return {
                ...device,
                controls: updatedControls,
                sensors: updatedSensors,
                linkquality: parsedData.status.linkquality || device.linkquality,
                lastUpdated: parsedData.status.lastUpdated || new Date().toISOString(),
                // Any report from the device means it is reachable again.
                noResponse: false
              };
            }
            return device;
          });
        });
      } else if (parsedData.type === 'deviceCommandFailed') {
        // Zigbee2MQTT could not deliver a command. The hub has already put the
        // real state back (that arrived as a deviceUpdate just before this);
        // mark the tile so it reads "No Response" until the device is heard
        // from again. The hub addresses the parent, so a gang child is matched
        // by parentId + the control the command touched.
        const failed = parsedData.data || {};
        const touched = Object.keys(failed.command || {});
        setDevices(prevDevices =>
          prevDevices.map(device => {
            const isTarget =
              device.id === failed.deviceId ||
              (device.parentId === failed.parentId &&
                (device.controls || []).some(c => touched.includes(c.property)));
            return isTarget ? { ...device, noResponse: true } : device;
          })
        );
      } else if (parsedData.type === 'sceneUpdate') {
        setScenes((prev) =>
          prev.map((scene) =>
            scene.id === parsedData.sceneId
              ? { ...scene, ...parsedData.updates }
              : scene
          )
        );
      } else if (parsedData.type === 'systemNotification') {
        // Check if notifications are enabled before showing
        const notificationsEnabled = config?.system?.notificationsEnabled !== false;
        if (!notificationsEnabled) {

          return;
        }

        switch (parsedData.level) {
          case 'error':
            toast.error(parsedData.message);
            break;
          case 'warning':
            toast.warning(parsedData.message);
            break;
          case 'success':
            toast.success(parsedData.message);
            break;
          default:
            toast.info(parsedData.message);
        }
      }
      // Handle smart lock updates
      else if (parsedData.type === 'lockDiscovered') {
        // Add or update lock in the list
        if (parsedData.data) {
          const lockData = parsedData.data;
          setSmartLocks(prevLocks => {
            const existingLockIndex = prevLocks.findIndex(lock => lock.lockMac === lockData.lockMac);
            if (existingLockIndex >= 0) {
              // Update existing lock
              const updatedLocks = [...prevLocks];
              updatedLocks[existingLockIndex] = { ...updatedLocks[existingLockIndex], ...lockData };
              return updatedLocks;
            } else {
              // Add new lock
              return [...prevLocks, lockData];
            }
          });
        }
      } else if (parsedData.type === 'lockStatusChanged') {
        // Update lock status with complete lock object from backend
        if (parsedData.data) {
          const lockData = parsedData.data;
          setSmartLocks(prevLocks => {
            const updatedLocks = prevLocks.map(lock => {
              if (lock.lockMac === lockData.lockMac) {
                // If backend sends complete lock object, use it
                if (lockData.lock) {
                  return lockData.lock;
                }

                // Fallback: patch individual fields (for backward compatibility).
                // Keep canonical lockState and the legacy isLocked mirror in sync.
                const updatedLock = { ...lock };
                const state = lockData.lockState || lockData.status;
                if (state === 'locked') {
                  updatedLock.lockState = 'locked';
                  updatedLock.isLocked = true;
                } else if (state === 'unlocked') {
                  updatedLock.lockState = 'unlocked';
                  updatedLock.isLocked = false;
                } else if (lockData.status === 'initialized') {
                  updatedLock.initialized = true;
                }
                if (lockData.lockAlias) {
                  updatedLock.lockAlias = lockData.lockAlias;
                }
                return updatedLock;
              }
              return lock;
            });
            return updatedLocks;
          });

          // Show notification for important status changes
          // if (['locked', 'unlocked'].includes(lockData.status)) {
          //   toast.success(`Lock ${lockData.lockAlias || lockData.lockMac} is now ${lockData.status}`);
          // }
        }
      } else if (parsedData.type === 'locksStatus') {
        // Update entire locks list
        if (parsedData.data && Array.isArray(parsedData.data)) {
          setSmartLocks(parsedData.data);
        }
      } else if (parsedData.type === 'lockAccessLogUpdated') {
        // New access log records available for a lock
        if (parsedData.data && parsedData.data.lockMac) {
          const { lockMac, newRecords } = parsedData.data;
          setLockAccessLogEvents(prev => ({
            ...prev,
            [lockMac]: {
              version: (prev[lockMac]?.version || 0) + 1,
              newRecords: Array.isArray(newRecords) ? newRecords : []
            }
          }));
        }
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }, [config]);

  const getToken = () => localStorage.getItem('authToken'); // or from Redux/context

  // WebSocket URL for hub-backend (port 3001)
  const API_WS_URL = process.env.NODE_ENV === 'development'
    ? 'ws://localhost:3001'
    : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;

  // Initialize WebSocket only when authenticated
  const { isConnected } = useWebSocket(
    isAuthenticated ? `${API_WS_URL}/ws/devices` : null,
    isAuthenticated ? handleWebSocketMessage : null,
    isAuthenticated ? getToken : null
  );

  // Reset all data when user logs out
  const resetData = useCallback(() => {
    setDevices([]);
    setScenes([]);
    setConfig({});
    setSmartLocks([]);
    setFetchError(null);
    setIsLoading(false);
    hasFetchedOnce.current = false;
  }, []);

  // Initial fetch - only when authenticated and not loading
  useEffect(() => {
    // Don't fetch if auth is still loading or user is not authenticated
    if (isAuthLoading || !isAuthenticated) {
      if (!isAuthenticated && !isAuthLoading) {
        // User is definitely not authenticated, reset data
        resetData();
      }
      return;
    }

    // Don't fetch if already fetched once
    if (hasFetchedOnce.current) return;

    const initializeFetch = async () => {
      try {
        setIsLoading(true);
        setFetchError(null);

        await Promise.all([
          fetchDevices(),
          fetchScenes(),
          fetchConfig(),
          fetchSmartLocks()
        ]);
        hasFetchedOnce.current = true;
      } catch (error) {
        console.error('Initial fetch failed:', error);
        setFetchError(error);
        // Only show toast error if user is still authenticated (avoid showing errors during logout)
        if (isAuthenticated) {
          toast.error('Failed to load initial data');
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeFetch();
  }, [isAuthenticated, isAuthLoading, fetchConfig, fetchDevices, fetchScenes, fetchSmartLocks, resetData]);

  // Control device - updated for the new API
  const controlDevice = useCallback(async (deviceId, property, value, parentId) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/devices/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deviceId,
          property,
          value,
          parentId
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to control device');
      }
      
      const result =data;
      
      // Update the local device state optimistically
      setDevices(prevDevices => {
        return prevDevices.map(device => {
          if (device.id === deviceId) {
            // Find the control to update
            const updatedControls = device.controls ? 
              device.controls.map(control => {
                if (control.property === property) {
                  return {
                    ...control,
                    value: value
                  };
                }
                return control;
              }) : 
              device.controls;
            
            return {
              ...device,
              controls: updatedControls,
              lastUpdated: new Date().toISOString()
            };
          }
          return device;
        });
      });
      
      return result;
    } catch (error) {
      console.error('Control device error:', error);
      toast.error(`Failed to control device: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Delete device
  const deleteDevice = useCallback(async (deviceId) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const { response, data } = await apiRequest(`${API_BASE_URL}${API_PATH}/devices/${deviceId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete device');
      }
      
      // Remove device(s) from local state
      // The backend may delete multiple devices (parent + children for multi-gang devices)
      const deletedDeviceIds = data.devicesDeleted || [deviceId];
      setDevices(prevDevices => {
        return prevDevices.filter(device => !deletedDeviceIds.includes(device.id));
      });
      
      if (deletedDeviceIds.length > 1) {
        // toast.success(`${deletedDeviceIds.length} devices deleted successfully`);
      } else {
        // toast.success('Device deleted successfully');
      }
      return data;
    } catch (error) {
      console.error('Delete device error:', error);
      toast.error(`Failed to delete device: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Activate scene
  const activateScene = useCallback(async (sceneId) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    
    try {
      const {response, data}= await apiRequest(
        `${API_BASE_URL}${API_PATH}/scenes/${sceneId}/activate`,
        { method: 'POST' }
      );

      if (!response.ok) {
        throw new Error('Failed to activate scene');
      }

      return data;
    } catch (error) {
      
      toast.error(`Failed to activate scene: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Debounced refresh methods
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refreshDevices = useCallback(
    debounce(() => fetchDevices(), 500),
    []
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refreshScenes = useCallback(
    debounce(() => fetchScenes(), 500),
    []
  );

  // Update device metadata
  const updateDeviceMetadata = useCallback(async (deviceId, metadata) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/devices/metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deviceId,
          ...metadata
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update device metadata');
      }
      
      // The API returns the complete updated device list
      const updatedDevices = data;
      
      // Update devices state with the new list
      setDevices(updatedDevices);
      
      // toast.success(`Device ${metadata.name || deviceId} updated successfully`);
      return updatedDevices;
    } catch (error) {
      console.error('Update device metadata error:', error);
      toast.error(`Failed to update device: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Create a new scene
  const createScene = useCallback(async (sceneData) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/scenes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sceneData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to create scene');
      }
      
      const newScene = data;
      
      // Update scenes state
      setScenes(prev => [...prev, newScene]);
      
      // toast.success(`Scene "${newScene.name}" created successfully`);
      return newScene;
    } catch (error) {
      console.error('Create scene error:', error);
      toast.error(`Failed to create scene: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Update a scene
  const updateScene = useCallback(async (sceneId, updates) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/scenes/${sceneId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update scene');
      }
      
      const updatedScene = data;
      
      // Update scenes state
      setScenes(prev => 
        prev.map(scene => scene.id === sceneId ? updatedScene : scene)
      );
      
      // toast.success(`Scene "${updatedScene.name}" updated successfully`);
      return updatedScene;
    } catch (error) {
      console.error('Update scene error:', error);
      toast.error(`Failed to update scene: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Delete a scene
  const deleteScene = useCallback(async (sceneId) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response}= await apiRequest(`${API_BASE_URL}${API_PATH}/scenes/${sceneId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete scene');
      }
      
      // Update scenes state
      setScenes(prev => prev.filter(scene => scene.id !== sceneId));
      
      // toast.success('Scene deleted successfully');
      return true;
    } catch (error) {
      console.error('Delete scene error:', error);
      toast.error(`Failed to delete scene: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Add an action to a scene
  const addSceneAction = useCallback(async (sceneId, action) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/scenes/${sceneId}/add-action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to add action to scene');
      }
      
      const updatedScene = data;
      
      // Update scenes state
      setScenes(prev => 
        prev.map(scene => scene.id === sceneId ? updatedScene : scene)
      );
      
      return updatedScene;
    } catch (error) {
      console.error('Add scene action error:', error);
      toast.error(`Failed to add action to scene: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Update a scene action
  const updateSceneAction = useCallback(async (sceneId, actionIndex, action) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/scenes/${sceneId}/actions/${actionIndex}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update scene action');
      }
      
      const updatedScene = data;
      
      // Update scenes state
      setScenes(prev => 
        prev.map(scene => scene.id === sceneId ? updatedScene : scene)
      );
      
      return updatedScene;
    } catch (error) {
      console.error('Update scene action error:', error);
      toast.error(`Failed to update scene action: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Remove an action from a scene
  const removeSceneAction = useCallback(async (sceneId, actionIndex) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/scenes/${sceneId}/actions/${actionIndex}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to remove action from scene');
      }
      
      const updatedScene = data;
      
      // Update scenes state
      setScenes(prev => 
        prev.map(scene => scene.id === sceneId ? updatedScene : scene)
      );
      
      return updatedScene;
    } catch (error) {
      console.error('Remove scene action error:', error);
      toast.error(`Failed to remove action from scene: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Fetch specific configuration section
  const fetchConfigSection = useCallback(async (section) => {
    if (!isAuthenticated) return {};
    
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/config/${section}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch ${section} configuration`);
      }
      
      // Update only the specific section in the state
      setConfig(prev => ({
        ...prev,
        [section]: data
      }));
      
      return data;
    } catch (error) {
      console.error(`Error fetching ${section} configuration:`, error);
      if (isAuthenticated) {
        toast.error(`Failed to load ${section} configuration`);
      }
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Update configuration
  const updateConfig = useCallback(async (configData) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ config: configData }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update configuration');
      }
      
      const result = data;
      
      if (result.success) {
        // Update local state
        setConfig(prev => ({
          ...prev,
          ...configData
        }));
        
        // toast.success('Configuration updated successfully');
      } else {
        throw new Error(result.message || 'Update failed');
      }
      
      return result;
    } catch (error) {
      console.error('Error updating configuration:', error);
      toast.error(`Failed to update configuration: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Update specific configuration section
  const updateConfigSection = useCallback(async (section, sectionData) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response,data}= await apiRequest(`${API_BASE_URL}${API_PATH}/config/${section}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sectionData),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update ${section} configuration`);
      }
      
      const result = data;
      
      if (result.success) {
        // Update local state for the section
        setConfig(prev => ({
          ...prev,
          [section]: {
            ...(prev[section] || {}),
            ...sectionData
          }
        }));
        
        // toast.success(`${section} configuration updated successfully`);
      } else {
        throw new Error(result.message || 'Update failed');
      }
      
      return result;
    } catch (error) {
      console.error(`Error updating ${section} configuration:`, error);
      toast.error(`Failed to update ${section} configuration: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Debounced refresh config
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refreshConfig = useCallback(
    debounce(() => fetchConfig(), 500),
    []
  );
  
  // Start scanning for smart locks
  const startSmartLockScan = useCallback(async (duration = 15000) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/scan/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ duration }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to start scan');
      }
      
      const result = data;
      
      toast.info(`Scanning for locks for ${duration / 1000} seconds...`);
      
      // After scan completes, refresh the lock list
      setTimeout(() => {
        fetchSmartLocks();
      }, duration + 500); // Add a small buffer
      
      return result;
    } catch (error) {
      console.error('Error starting smart lock scan:', error);
      toast.error('Failed to start scan');
      throw error;
    }
  }, [fetchSmartLocks, apiRequest, isAuthenticated]);
  
  // Stop scanning for smart locks
  const stopSmartLockScan = useCallback(async () => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/scan/stop`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error('Failed to stop scan');
      }
      
      return data;
    } catch (error) {
      console.error('Error stopping smart lock scan:', error);
      toast.error('Failed to stop scan');
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Get scan status (for discovering locks)
  const getScanStatus = useCallback(async () => {
    if (!isAuthenticated) return {};

    try {
      const { response, data } = await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/scan/status`);

      if (!response.ok) {
        throw new Error('Failed to get scan status');
      }

      return data;
    } catch (error) {
      console.error('Error getting scan status:', error);
      if (isAuthenticated) {
        toast.error('Failed to get scan status');
      }
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Initialize a smart lock
  const initializeSmartLock = useCallback(async (mac, alias) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    // Prevent multiple concurrent initializations
    if (initializingLockMac) {
      throw new Error('A lock initialization is already in progress');
    }

    try {
      setInitializingLockMac(mac);

      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ lockAlias: alias }),
      });

      if (!response.ok) {
        throw new Error('Failed to initialize lock');
      }

      const result = data;

      if (result.success) {
        // toast.success(`Lock ${alias || mac} initialized successfully`);
        await fetchSmartLocks();
      } else {
        toast.error('Failed to initialize lock');
      }

      return result;
    } catch (error) {
      console.error('Error initializing smart lock:', error);
      toast.error(`Failed to initialize lock: ${error.message}`);
      throw error;
    } finally {
      setInitializingLockMac(null);
    }
  }, [fetchSmartLocks, apiRequest, isAuthenticated, initializingLockMac]);
  
  // Lock a smart lock
  const lockSmartLock = useCallback(async (mac) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/lock`, {
        method: 'POST',
      });

      if (!response.ok || !data?.success) {
        throw new Error('Failed to lock device');
      }

      const result = data;

      if (result.lock) {
        setSmartLocks(prev => prev.map(lock =>
          lock.lockMac === mac ? result.lock : lock
        ));
      }

      return result;
    } catch (error) {
      console.error('Error locking smart lock:', error);
      toast.error(error.message || 'Failed to lock device');
      throw error;
    }
  }, [apiRequest, isAuthenticated, setSmartLocks]);
  
  // Unlock a smart lock
  const unlockSmartLock = useCallback(async (mac) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/unlock`, {
        method: 'POST',
      });

      if (!response.ok || !data?.success) {
        throw new Error('Failed to unlock device');
      }

      const result = data;

      if (result.lock) {
        setSmartLocks(prev => prev.map(lock =>
          lock.lockMac === mac ? result.lock : lock
        ));
      }

      return result;
    } catch (error) {
      console.error('Error unlocking smart lock:', error);
      toast.error(error.message || 'Failed to unlock device');
      throw error;
    }
  }, [apiRequest, isAuthenticated, setSmartLocks]);
  
  // Fetch access logs for a smart lock
  const fetchLockLogs = useCallback(async (mac, params = {}) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    try {
      const query = new URLSearchParams();
      if (params.limit) query.set('limit', params.limit);
      if (params.before) query.set('before', params.before);
      if (params.category) query.set('category', params.category);
      const queryString = query.toString();

      const { response, data } = await apiRequest(
        `${API_BASE_URL}${API_PATH}/smartlocks/${mac}/logs${queryString ? `?${queryString}` : ''}`
      );

      if (!response.ok || !data?.success) {
        throw new Error('Failed to fetch lock activity');
      }

      return data.data;
    } catch (error) {
      console.error('Error fetching lock activity:', error);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Refresh access logs for a smart lock (triggers a BLE read, can take up to ~90s)
  const refreshLockLogs = useCallback(async (mac) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    try {
      const { response, data } = await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/logs/refresh`, {
        method: 'POST',
      });

      if (!response.ok || !data?.success) {
        throw new Error('Failed to refresh lock activity');
      }

      return data.data;
    } catch (error) {
      console.error('Error refreshing lock activity:', error);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Toggle a smart lock (lock/unlock)
  const toggleSmartLock = useCallback(async (mac, currentStatus) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    try {
      if (currentStatus) {
        return await unlockSmartLock(mac);
      } else {
        return await lockSmartLock(mac);
      }
    } catch (error) {
      console.error('Error toggling smart lock:', error);
      throw error;
    }
  }, [lockSmartLock, unlockSmartLock, isAuthenticated]);

  // Fire-and-forget BLE session warm-up: called when lock UI is shown so the
  // user's tap hits the hub's held-session fast path instead of a cold connect
  const prepareSmartLock = useCallback(async (mac) => {
    if (!isAuthenticated) return;
    try {
      await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/prepare`, {
        method: 'POST',
      });
    } catch (error) {
      // Warm-up is best-effort; a failure just means a cold connect on tap
    }
  }, [isAuthenticated]);
  
  // Update smart lock alias (name)
  const updateSmartLockAlias = useCallback(async (mac, alias) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/alias`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ alias }),
      });

      if (!response.ok) {
        throw new Error('Failed to update lock alias');
      }

      const result = data;

      if (result.success) {
        if (result.lock) {
          setSmartLocks(prev => prev.map(lock =>
            lock.lockMac === mac ? result.lock : lock
          ));
        }
        // toast.success(`Lock name updated to ${alias}`);
      } else {
        toast.error('Failed to update lock name');
      }

      return result;
    } catch (error) {
      console.error('Error updating smart lock alias:', error);
      toast.error(`Failed to update lock name: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated, setSmartLocks]);
  
  // Set auto lock time
  const setAutoLockTime = useCallback(async (mac, seconds) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/settings/auto-lock-time`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ seconds }),
      });

      if (!response.ok) {
        throw new Error('Failed to set auto lock time');
      }

      const result = data;

      if (result.success) {
        if (result.lock) {
          setSmartLocks(prev => prev.map(lock =>
            lock.lockMac === mac ? result.lock : lock
          ));
        }
        // toast.success(`Auto-lock set to ${seconds} seconds`);
      } else {
        toast.error('Failed to set auto lock time');
      }

      return result;
    } catch (error) {
      console.error('Error setting auto lock time:', error);
      toast.error(`Failed to set auto lock time: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated, setSmartLocks]);

  // Set lock sound
  const setLockSound = useCallback(async (mac, enabled) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/settings/lock-sound`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled }),
      });

      if (!response.ok) {
        throw new Error('Failed to set lock sound');
      }

      const result = data;

      if (result.success) {
        if (result.lock) {
          setSmartLocks(prev => prev.map(lock =>
            lock.lockMac === mac ? result.lock : lock
          ));
        }
        // toast.success(`Lock sound ${enabled ? 'enabled' : 'disabled'}`);
      } else {
        toast.error('Failed to set lock sound');
      }

      return result;
    } catch (error) {
      console.error('Error setting lock sound:', error);
      toast.error(`Failed to set lock sound: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated, setSmartLocks]);

  // Add a passcode to a lock
  const addPasscode = useCallback(async (mac, passcode, name, startDate, endDate, type = 'permanent', recurringConfig = null) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    try {
      const requestBody = {
        passcode,
        name,
        type
      };

      if (startDate) {
        requestBody.startDate = startDate;
      }
      if (endDate) {
        requestBody.endDate = endDate;
      }
      if (recurringConfig && type === 'recurring') {
        requestBody.recurringConfig = recurringConfig;
      }

      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/passcodes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to add passcode');
      }

      const result = data;

      if (result.success) {
        if (result.lock) {
          setSmartLocks(prev => prev.map(lock =>
            lock.lockMac === mac ? result.lock : lock
          ));
        }
        // toast.success(`Passcode ${name} added successfully`);
      } else {
        toast.error('Failed to add passcode');
      }

      return result;
    } catch (error) {
      console.error('Error adding passcode:', error);
      toast.error(`Failed to add passcode: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated, setSmartLocks]);
  
  // Delete a passcode from a lock
  const deletePasscode = useCallback(async (mac, id) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/passcodes/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete passcode');
      }

      const result = data;

      if (result.success) {
        if (result.lock) {
          setSmartLocks(prev => prev.map(lock =>
            lock.lockMac === mac ? result.lock : lock
          ));
        }
        // toast.success('Passcode deleted successfully');
      } else {
        toast.error('Failed to delete passcode');
      }

      return result;
    } catch (error) {
      console.error('Error deleting passcode:', error);
      toast.error(`Failed to delete passcode: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated, setSmartLocks]);

  // Add a fingerprint to a lock
  const addFingerprint = useCallback(async (mac, name, startDate = null, endDate = null, recurringConfig = null) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    try {
      const requestBody = { name };

      if (startDate) {
        requestBody.startDate = startDate;
      }
      if (endDate) {
        requestBody.endDate = endDate;
      }
      if (recurringConfig) {
        requestBody.recurringConfig = recurringConfig;
      }

      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/fingerprints`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to add fingerprint');
      }

      const result = data;

      if (result.success) {
        if (result.lock) {
          setSmartLocks(prev => prev.map(lock =>
            lock.lockMac === mac ? result.lock : lock
          ));
        }
        // toast.success(`Fingerprint ${name} added successfully`);
      } else {
        // toast.error('Failed to add fingerprint');
      }

      return result;
    } catch (error) {
      console.error('Error adding fingerprint:', error);
      toast.error(`Failed to add fingerprint: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated, setSmartLocks]);

  // Delete a fingerprint from a lock
  const deleteFingerprint = useCallback(async (mac, id) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/fingerprints/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete fingerprint');
      }

      const result = data;

      if (result.success) {
        if (result.lock) {
          setSmartLocks(prev => prev.map(lock =>
            lock.lockMac === mac ? result.lock : lock
          ));
        }
        // toast.success('Fingerprint deleted successfully');
      } else {
        // toast.error('Failed to delete fingerprint');
      }

      return result;
    } catch (error) {
      console.error('Error deleting fingerprint:', error);
      toast.error(`Failed to delete fingerprint: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated, setSmartLocks]);

  // Add a card to a lock
  const addCard = useCallback(async (mac, name, startDate = null, endDate = null, recurringConfig = null) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    try {
      const requestBody = { name };

      if (startDate) {
        requestBody.startDate = startDate;
      }
      if (endDate) {
        requestBody.endDate = endDate;
      }
      if (recurringConfig) {
        requestBody.recurringConfig = recurringConfig;
      }

      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/cards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to add card');
      }

      const result = data;

      if (result.success) {
        if (result.lock) {
          setSmartLocks(prev => prev.map(lock =>
            lock.lockMac === mac ? result.lock : lock
          ));
        }
        // toast.success(`Card ${name} added successfully`);
      } else {
        // toast.error('Failed to add card');
      }

      return result;
    } catch (error) {
      console.error('Error adding card:', error);
      toast.error(`Failed to add card: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated, setSmartLocks]);

  // Delete a card from a lock
  const deleteCard = useCallback(async (mac, id) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/cards/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete card');
      }

      const result = data;

      if (result.success) {
        if (result.lock) {
          setSmartLocks(prev => prev.map(lock =>
            lock.lockMac === mac ? result.lock : lock
          ));
        }
        // toast.success('Card deleted successfully');
      } else {
        // toast.error('Failed to delete card');
      }

      return result;
    } catch (error) {
      console.error('Error deleting card:', error);
      toast.error(`Failed to delete card: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated, setSmartLocks]);

  // Delete a smart lock from the system
  const deleteSmartLock = useCallback(async (mac) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete smart lock');
      }

      const result = data;

      if (result.success) {
        // Remove lock from local state
        setSmartLocks(prevLocks => prevLocks.filter(lock => lock.lockMac !== mac));

        // toast.success('Smart lock removed from system');
      } else {
        // toast.error('Failed to delete smart lock');
      }

      return result;
    } catch (error) {
      console.error('Error deleting smart lock:', error);
      toast.error(`Failed to delete smart lock: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  const resetSmartLock = useCallback(async (mac) => {
    if (!isAuthenticated) throw new Error('Not authenticated');

    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/smartlocks/${mac}/reset`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to reset smart lock');
      }

      const result = data;

      if (result.success) {
        // Remove lock from local state
        setSmartLocks(prevLocks => prevLocks.filter(lock => lock.lockMac !== mac));

        // toast.success('Smart lock reset to factory settings');
      } else {
        toast.error('Failed to reset smart lock');
      }

      return result;
    } catch (error) {
      console.error('Error resetting smart lock:', error);
      toast.error(`Failed to reset smart lock: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Debounced refresh smart locks
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refreshSmartLocks = useCallback(
    debounce(() => fetchSmartLocks(), 500),
    []
  );

  // Fetch Zigbee2MQTT configuration
  const fetchZigbee2mqttConfig = useCallback(async () => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/zigbee2mqtt/config`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch Zigbee2MQTT configuration');
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching Zigbee2MQTT configuration:', error);
      toast.error('Failed to load Zigbee2MQTT configuration');
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Update Zigbee2MQTT configuration
  const updateZigbee2mqttConfig = useCallback(async (configData) => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/zigbee2mqtt/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ config: configData }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update Zigbee2MQTT configuration');
      }
      
      const result = data;
      
      if (result.success) {
        // toast.success('Zigbee2MQTT configuration updated successfully');
      } else {
        throw new Error(result.message || 'Update failed');
      }
      
      return result;
    } catch (error) {
      console.error('Error updating Zigbee2MQTT configuration:', error);
      toast.error(`Failed to update Zigbee2MQTT configuration: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);
  
  // Restart Zigbee2MQTT service
  const restartZigbee2mqtt = useCallback(async () => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    
    try {
      const {response, data}= await apiRequest(`${API_BASE_URL}${API_PATH}/zigbee2mqtt/restart`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error('Failed to restart Zigbee2MQTT service');
      }
      
      const result = data;
      
      if (result.success) {
        // toast.success('Zigbee2MQTT service restart initiated');
      } else {
        throw new Error(result.message || 'Restart failed');
      }
      
      return result;
    } catch (error) {
      console.error('Error restarting Zigbee2MQTT service:', error);
      toast.error(`Failed to restart Zigbee2MQTT service: ${error.message}`);
      throw error;
    }
  }, [apiRequest, isAuthenticated]);

  // Provide context
  const contextValue = {
    devices,
    scenes,
    config,
    smartLocks,
    isConnected,
    isLoading,
    fetchError,
    isAuthenticated,
    initializingLockMac,
    controlDevice,
    deleteDevice,
    activateScene,
    refreshDevices,
    refreshScenes,
    refreshConfig,
    updateDeviceMetadata,
    resetData,
    // Scene management
    createScene,
    updateScene,
    deleteScene,
    addSceneAction,
    updateSceneAction,
    removeSceneAction,
    // Configuration management
    fetchConfig,
    fetchConfigSection,
    updateConfig,
    updateConfigSection,
    // Zigbee2MQTT configuration management
    fetchZigbee2mqttConfig,
    updateZigbee2mqttConfig,
    restartZigbee2mqtt,
    // Smart lock management
    fetchSmartLocks,
    refreshSmartLocks,
    startSmartLockScan,
    stopSmartLockScan,
    getScanStatus,
    initializeSmartLock,
    lockSmartLock,
    unlockSmartLock,
    toggleSmartLock,
    prepareSmartLock,
    fetchLockLogs,
    refreshLockLogs,
    lockAccessLogEvents,
    updateSmartLockAlias,
    setAutoLockTime,
    setLockSound,
    addPasscode,
    deletePasscode,
    addFingerprint,
    deleteFingerprint,
    addCard,
    deleteCard,
    deleteSmartLock,
    resetSmartLock,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;